import { StockResultStore } from '~/types/Stock'
import { LocationSubmitType, SpecialStoreHour } from '~/types/Store'

const CNC_CUTOFF_HOUR = 13 // 1pm local store time

export const isStoreOpenNow = (store: SFCCStore, locale: Locales, now: Date = new Date()): boolean => {
  const currentDay = now.toLocaleString(localeConfig[locale].code, { weekday: 'long' })
  const storeDayStart: string | undefined = store[`c_storeHours${currentDay}Start`]
  const storeDayEnd: string | undefined = store[`c_storeHours${currentDay}End`]

  if (!storeDayStart || !storeDayEnd) return false

  const currentHour = now.getHours()
  const storeDayStartHour = parseStoreHourToInt(storeDayStart)
  const storeDayEndHour = parseStoreHourToInt(storeDayEnd)

  if (storeDayStartHour === null || storeDayEndHour === null) return false

  return currentHour >= storeDayStartHour && currentHour <= storeDayEndHour
}

/**
 * Returns the i18n key for the Click & Collect dynamic message based on the
 * current local time and special hours. 1pm cutoff for same day click and collect.
 */
export const getClickAndCollectMessageKey = (store: SFCCStore, now: Date = new Date()): string => {
  const currentHour = now.getHours()

  if (currentHour >= CNC_CUTOFF_HOUR) {
    return 'product_details_page.find_store.collect_next_day'
  }

  // Check for special hours
  if (store.c_specialStoreHoursJson) {
    try {
      const specialHours: SpecialStoreHour[] = JSON.parse(store.c_specialStoreHoursJson)
      const todayStr = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0'),
      ].join('-')
      const todayEntry = specialHours.find((h) => h.special_date === todayStr)
      if (todayEntry) {
        if (todayEntry.isClosed) return 'product_details_page.find_store.collect_next_day'
        const closeHour = parseStoreHourToInt(todayEntry.close)
        return closeHour !== null && closeHour > 14
          ? 'product_details_page.find_store.same_day_collect'
          : 'product_details_page.find_store.collect_next_day'
      }
    } catch {
      // If error in special hours JSON — fall through to regular hours
    }
  }

  // Before 1pm and no special-hours override: show same-day message
  return 'product_details_page.find_store.same_day_collect'
}

/** Parses a 24 hour time string  */
export const parseStoreHourToInt = (time: string): number | null => {
  const cleaned = time.trim()
  const parts = cleaned.split(/:|\s/)
  if (parts.length < 2) return null
  let hours = parseInt(parts[0] ?? '', 10)
  if (isNaN(hours)) return null
  const period = parts[2]?.toUpperCase()
  if (period === 'PM' && hours !== 12) hours += 12
  else if (period === 'AM' && hours === 12) hours = 0
  return hours
}

const GEOLOCATION_TIMEOUT_MS = 10_000
// GeolocationPositionError.PERMISSION_DENIED. Browsers also use it for a dismissed prompt or OS-level location off.
const GEOLOCATION_PERMISSION_DENIED = 1

type GeoPositionResult = { position: GeolocationPosition } | { failure: 'denied' | 'unavailable' }

export const getCurrentGeoPosition = (): Promise<GeoPositionResult> =>
  new Promise((resolve) => {
    if (!navigator?.geolocation) {
      resolve({ failure: 'unavailable' })
      return
    }
    // The browser starts this timeout only after permission is granted, so an open prompt never trips it.
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ position }),
      (error) => resolve({ failure: error.code === GEOLOCATION_PERMISSION_DENIED ? 'denied' : 'unavailable' }),
      { timeout: GEOLOCATION_TIMEOUT_MS },
    )
  })

// 'denied' before asking means the shopper blocked location earlier, so the browser will not prompt
export const getGeolocationPermission = async (): Promise<PermissionState | 'unknown'> => {
  try {
    return (await navigator.permissions?.query({ name: 'geolocation' }))?.state ?? 'unknown'
  } catch {
    return 'unknown'
  }
}

export type LocationResponse = 'accepted' | 'declined' | 'blocked' | 'unavailable'

export const handleLocationResponseDataLayer = (datalayerLocationResponse: {
  response: LocationResponse
  locale: Locales
  group: 'set my store' | 'pdp find in store' | 'click and collect' | 'store locator'
}) => {
  const { response, locale, group } = datalayerLocationResponse
  pushEventToDataLayer('custom.location.response', { group, type: response }, locale)
}

export const getStoreUrl = (store: SFCCStore, path: string) =>
  `${path}/${store?.name?.toLocaleLowerCase().replace(/\s+/g, '-') || ''}`

export const tidyStoreName = (name: string | undefined) => name?.replace(/^Michael Hill /, '')

/** SFCC store hours come as "5:30 PM"; the site shows them as "5:30pm". */
export const formatStoreTime = (time: string | undefined) => time?.replace(/\s/g, '').toLowerCase() ?? ''

const stateCodeFor = (state?: string) => [...STATES_AU, ...STATES_CA].find(({ name }) => name === state)?.code ?? state

export const formatStoreAddress = (store: SFCCStore) =>
  [
    store.address1,
    store.address2,
    store.city,
    [stateCodeFor(store.stateCode), store.postalCode].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ')

export const handleErrorDataLayer = (datalayerError: {
  errorMessage: string
  searchValue: string
  locale: Locales
  group: 'set my store' | 'pdp find in store' | 'click and collect' | 'store locator'
}) => {
  const { errorMessage, searchValue, locale, group } = datalayerError
  const event = 'custom.search.error'
  const data = {
    group,
    errors: [
      {
        label: errorMessage,
      },
    ],
    search: {
      term: searchValue,
    },
    data: undefined,
  }
  pushEventToDataLayer(event, data, locale)
}

export const handleSubmitDataLayer = (datalayerSubmitObject: {
  submitType: LocationSubmitType
  searchValue: string
  locale: Locales
  group: 'set my store' | 'pdp find in store' | 'click and collect' | 'store locator'
}) => {
  const { submitType, searchValue, locale, group } = datalayerSubmitObject
  const event = 'custom.search.submit'
  pushEventToDataLayer(
    event,
    {
      group,
      type: submitType,
      search: {
        term: searchValue,
      },
    },
    locale,
  )
}

export const handleResultViewDataLayer = (datalayerResultViewObject: {
  storeResults: SFCCStore[]
  storeStockResults: StockResult
  searchValue: string
  locale: Locales
  group: 'set my store' | 'pdp find in store' | 'click and collect' | 'store locator'
  submitType: LocationSubmitType
}) => {
  const { storeResults, storeStockResults, searchValue, locale, group, submitType } = datalayerResultViewObject
  const event = 'custom.search.results.view'

  const resultCount = storeResults.length ?? 0
  const mappedStoreResults = mapStoreResultDatalayer({ storeResults, storeStockResults })

  pushEventToDataLayer(
    event,
    {
      group,
      type: submitType,
      search: {
        term: searchValue,
        resultCount,
        results: mappedStoreResults,
      },
    },
    locale,
  )
}

export const mapStoreResultDatalayer = (datalayerStoreResultObject: {
  storeResults: SFCCStore[]
  storeStockResults?: StockResult
}) => {
  const { storeResults, storeStockResults } = datalayerStoreResultObject
  return storeResults.map((store, index) => {
    const stockResultStore = storeStockResults?.stores.find((stockStore) => stockStore.store === store.id)

    return {
      store: {
        name: store.name,
        id: store.id,
        suburb: store.city,
        postcode: store.postalCode,
      },
      stockStatus: getDataLayerStockStatus(stockResultStore),
      clickAndCollectStatus: store.c_clickAndCollectEnabled ? 'available' : 'unavailable',
      position: index + 1,
    }
  })
}

const getDataLayerStockStatus = (stockResultStore?: StockResultStore) => {
  if (!stockResultStore) {
    return 'n/a'
  } else if (stockResultStore.stockLevels[0]?.quantity === 0) {
    return 'out of stock'
  } else if ((stockResultStore.stockLevels[0]?.quantity || 0) >= 2) {
    return 'in stock'
  } else {
    return 'limited stock'
  }
}

export const handleResultClickDataLayer = (datalayerResultClickObject: {
  storeResults: SFCCStore[]
  storeStockResults?: StockResult
  searchValue: string
  storeIndex: number
  linkDetails: { label: string; url?: string }
  isMapPopup?: boolean
  locale: Locales
  group: 'set my store' | 'pdp find in store' | 'click and collect' | 'store locator'
}) => {
  const { storeResults, storeStockResults, searchValue, storeIndex, linkDetails, isMapPopup, locale, group } =
    datalayerResultClickObject
  const event = !isMapPopup ? 'custom.search.results.click' : 'custom.map.popup.click'
  const resultCount = storeResults.length ?? 0

  const mappedStoreResults = mapStoreResultDatalayer({ storeResults, storeStockResults })
  pushEventToDataLayer(
    event,
    {
      group,
      search: {
        term: searchValue,
        clickedResult: {
          ...mappedStoreResults[storeIndex]!,
          label: linkDetails.label,
          url: linkDetails.url,
        },
        resultCount,
        results: mappedStoreResults,
      },
    },
    locale,
  )
}

export const handleMapPopupDataLayer = (datalayerMapPopupObject: {
  storeResults: SFCCStore[]
  storeStockResults?: StockResult
  searchValue: string
  storeIndex: number
  action: 'open' | 'close'
  locale: Locales
  group: 'store locator'
}) => {
  const { storeResults, storeStockResults, searchValue, storeIndex, action, locale, group } = datalayerMapPopupObject
  const event: 'custom.map.popup.open' | 'custom.map.popup.close' = `custom.map.popup.${action}`
  const resultCount = storeResults.length ?? 0

  const mappedStoreResults = mapStoreResultDatalayer({ storeResults, storeStockResults })
  pushEventToDataLayer(
    event,
    {
      group,
      search: {
        term: searchValue,
        clickedResult: {
          ...mappedStoreResults[storeIndex]!,
          label: '',
          url: '',
        },
        resultCount,
        results: mappedStoreResults,
      },
    },
    locale,
  )
}
